import { useEffect, useRef } from "react";

/**
 * @typedef {object} AuthCacheHygieneArgs
 * @property {string|null|undefined} uid
 *   Current authenticated user ID. Changes trigger cache hygiene.
 * @property {Function} [clearProfile]
 *   Optional single-user profile-cache clear function.
 * @property {Function} [clearAllProfiles]
 *   Optional bulk profile-cache clear function.
 * @property {Function} [clearAllFirstRun]
 *   Optional bulk first-run cache clear function.
 */

/**
 * useAuthCacheHygiene
 * -------------------
 * Clears session-scoped caches when the authenticated uid changes (logout or user switch).
 *
 * Rationale:
 *  - Prevents stale sessionStorage/in-memory cache from one user leaking into another
 *    when they sign out/in within the same tab.
 *
 * @param {AuthCacheHygieneArgs} args
 */
export default function useAuthCacheHygiene({
  uid,
  clearProfile,
  clearAllProfiles,
  clearAllFirstRun,
}) {
  const prevUidRef = useRef(null);

  useEffect(() => {
    const prevUid = prevUidRef.current;
    const nextUid = uid ?? null;

    if (prevUid === nextUid) return;

    // if switching user or logging out, clear old user's profile cache
    if (prevUid) {
      if (typeof clearAllProfiles === "function") clearAllProfiles();
      else if (typeof clearProfile === "function") clearProfile(prevUid);
    }

    // Clear first-run cache only on uid change (login/logout/switch),
    // and only if the function exists.
    if (typeof clearAllFirstRun === "function") clearAllFirstRun();

    prevUidRef.current = nextUid;
  }, [uid, clearProfile, clearAllProfiles, clearAllFirstRun]); // <-- include it
}
